/**
 * AtendimentoPage — tela do parceiro no Portal (PRD 4, Story 4.1.5).
 *
 * Layout:
 * - lg: 2 colunas (lista esquerda 288px + chat principal flex-1)
 * - <lg: empilhado (lista em cima, chat em baixo)
 *
 * Features:
 * - Lista de sessões do parceiro (poll 15s)
 * - "Abrir nova sessão" via modal com dropdown de setores ativos
 * - Chat: histórico de mensagens (poll 5s) + composer
 * - Composer desabilita se sessão estiver RESOLVED ou CANCELLED
 *
 * Auth: depende de PortalAuthProvider + ProtectedPortalRoute (acessível
 * só pra parceiros logados via portal_access_token).
 *
 * Pattern reference: V1/src/pages/atendimentos/AtendimentosPage.tsx
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, Loader2, MessageSquarePlus, Send as SendIcon, Users } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AudioMessageBubble } from "@/components/common/AudioMessageBubble";
// Anexo (imagem/arquivo): render de imagem/preview/download — mesmo bubble do
// atendente. allowPreview=false: o parceiro não alcança o dispatcher /file-preview
// (a signed-url do <img>/download é HMAC, sem auth, então imagem+download rodam).
import {
  AttachmentMessageBubble,
  type AttachmentMessageContent,
} from "@/components/common/AttachmentMessageBubble";
// Prévia de link: o link clicável no texto e o cartão do site na bolha (origem `portal`: o parceiro nunca chama a rota interna).
import { CartaoDoLink } from "@/components/links/CartaoDoLink";
import { TextoComLinks } from "@/components/links/TextoComLinks";
import { BotTriagePanel } from "@/components/portal/BotTriagePanel";
import { ContadorDoAtendimento } from "@/components/portal/ContadorDoAtendimento";
import { CustomerChatComposer } from "@/components/portal/CustomerChatComposer";
import { FaixaDeChamada } from "@/components/portal/FaixaDeChamada";
import { ConsentModal } from "@/components/atendimentos/ConsentModal";
import { JanelaDeChamada, type ModoDaJanela } from "@/components/calls/JanelaDeChamada";
import { useRoomContext } from "@livekit/components-react";
import { Track } from "livekit-client";
import { portalAtendimentosApi } from "@/services/portalAtendimentosApi";
import { ScreenShareRequestBanner } from "@/components/calls/ScreenShareRequestBanner";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { useCallDataEvents } from "@/hooks/useCallDataEvents";
import { useGrudadoNoPe } from "@/hooks/useGrudadoNoPe";
import { useNaoLidasDoAtendimento } from "@/hooks/useNaoLidasDoAtendimento";
import {
  useChamadaEmAndamento,
  usePortalMessages,
  usePortalSectors,
  usePortalSessions,
  useStartPortalSession,
} from "@/hooks/usePortalAtendimento";
import { assinarSalaAberta, definirSalaAberta, salaAbertaVisivel } from "@/lib/avisos/salaAberta";
import { textoDasNaoLidas } from "@/lib/portal/naoLidasDoAtendimento";
import { cn } from "@/lib/utils";
import { primeiroLink } from "@/lib/links/links";
import {
  atendenteDaSessao,
  descricaoDaSessao,
  MARCA_SO_LEITURA,
  tituloCurtoDaConversaSoLeitura,
  tituloDaConversaSoLeitura,
  tituloDaSessao,
} from "@/lib/sessaoDisplay";
import { textoDaMensagem } from "@/lib/mensagemTexto";
import { distanciaDoPe } from "@/lib/rolagem/grudadoNoPe";
import type { CustomerSession, CustomerSessionMessage, SessionStatus } from "@/types/atendimento";

const CLOSED_STATUSES: ReadonlySet<SessionStatus> = new Set(["RESOLVED", "CANCELLED"]);

// Patch E8 — humaniza enum em pt-BR; senão parceiro vê tokens internos
// como "IN_PROGRESS" no UI.
const STATUS_LABEL: Record<SessionStatus, string> = {
  QUEUED: "Na fila",
  IN_PROGRESS: "Em atendimento",
  AWAITING: "Aguardando",
  RESOLVED: "Resolvido",
  CANCELLED: "Cancelado",
};

// Patch E1 — auto-scroll só dispara quando o usuário JÁ está perto do
// fim. Limiar de 80px abaixo do scrollTop máximo conta como "no final".
// Medido ANTES de a mensagem nova entrar; o estado "grudado" do `useGrudadoNoPe` (8px) é outro e cuida só do que cresce
// com a pessoa já no fim (cartão do site, imagem).
const NEAR_BOTTOM_THRESHOLD_PX = 80;

function statusVariant(s: SessionStatus): "default" | "secondary" | "outline" | "destructive" {
  if (s === "IN_PROGRESS") return "default";
  if (s === "QUEUED" || s === "AWAITING") return "secondary";
  if (s === "RESOLVED") return "outline";
  return "destructive";
}

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const dt = new Date(iso);
  const diffMs = Date.now() - dt.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH} h`;
  return dt.toLocaleDateString("pt-BR");
}

// O extrator de texto vive em `@/lib/mensagemTexto` desde 06/08/2026. Esta
// página tinha o próprio, e ele não conhecia o `bot_question` do bot de
// triagem (texto em `prompt`, não em `text`) — resultado: toda sessão nova
// mostrava uma pílula "[anexo]" acima da saudação. O mesmo content virava JSON
// cru em /atendimentos, que tinha OUTRA cópia da mesma lógica.

/**
 * A pergunta do bot NÃO entra no histórico desta página.
 *
 * Quem a desenha aqui é o `<BotTriagePanel>`, com as opções clicáveis — que é
 * a forma útil dela. Renderizar também como pílula produz o MESMO texto duas
 * vezes na tela, uma delas inerte.
 *
 * Foi o que apareceu ao conferir o conserto no navegador: trocar "[anexo]"
 * pelo texto certo revelou a duplicata, que o rótulo quebrado escondia. Em
 * `/atendimentos` o filtro não existe e nem deveria — lá não há painel, e o
 * atendente quer ver o que o bot perguntou.
 *
 * Só o extrator é compartilhado (ele sabe o TEXTO); a decisão de renderizar é
 * de cada tela.
 */
function naoEhPerguntaDoBot(m: CustomerSessionMessage): boolean {
  return m.content?.type !== "bot_question";
}

function MessageBubble({
  msg,
  isOwn,
}: {
  msg: CustomerSessionMessage;
  /** Patch B1+A4: vem do parent (PortalAuth), não derivado da sessão. */
  isOwn: boolean;
}) {
  // Nota de voz (A4): "ouvido" session-local (persistência deferida, como C2/A3).
  const [audioPlayed, setAudioPlayed] = useState(false);
  const isAudio = msg.content?.type === "audio" || msg.content?.__type === "audio";
  const audio = isAudio
    ? (msg.content as unknown as {
        url?: string;
        duration_sec?: number;
        mime?: string;
        caption?: string;
      })
    : null;
  // Anexo (imagem/arquivo): render de imagem-de-verdade + download (não mais só o
  // rótulo). Suporta url de storage (anexo do atendente, re-assinada) E o
  // attachment_b64 legado do próprio parceiro (via data-uri no bubble).
  // `video` incluído em 06/08/2026. O Augusto reportou que o vídeo enviado
  // aparecia "apenas como um anexo": esta página tem a PRÓPRIA bolha, que
  // duplica a decisão por tipo do `AttachmentMessageBubble`. Eu havia estendido
  // só aquela — e vídeo caía no `else`, virando o literal "[anexo]".
  //
  // Duas listas mantidas à mão sempre divergem; o teste que fecha isso compara
  // as duas (ver `MessageBubble.tipos.test.tsx`), em vez de conferir uma.
  const isAttachment =
    msg.content?.type === "image" || msg.content?.type === "file" || msg.content?.type === "video";
  const attachment = isAttachment ? (msg.content as unknown as AttachmentMessageContent) : null;
  const text = textoDaMensagem(msg.content);

  if (msg.sender_type === "SYSTEM") {
    return (
      <div className="flex justify-center my-2">
        <div className="text-xs italic text-muted-foreground bg-muted/40 px-3 py-1 rounded-full">
          {/* A resposta final do bot de triagem e os avisos do sistema chegam como SYSTEM e podem trazer um link: clicável,
              sem cartão (a pílula não tem onde pôr um). A pergunta do bot (`bot_question`) nem chega aqui: o painel a desenha. */}
          <TextoComLinks texto={text} tom="neutro" />
        </div>
      </div>
    );
  }

  // Prévia de link: só a mensagem de TEXTO ganha o cartão do site. Anexo (imagem, arquivo, vídeo) e áudio só têm o link
  // clicável (ou nem isso). Sem link, `null`: o caminho sem link não usa react-query nem router.
  const linkDoCartao = msg.content?.type === "text" ? primeiroLink(text) : null;
  const linkDispensado =
    typeof msg.content?.link_dispensado === "string" ? msg.content.link_dispensado : undefined;

  return (
    <div className={cn("flex my-1.5", isOwn ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[75%] px-3 py-2 rounded-2xl text-sm shadow-md backdrop-blur-md border",
          isOwn
            ? "bg-emerald-600/90 text-white border-emerald-50/25 rounded-br-md"
            : "bg-background/80 text-foreground border-emerald-100/50 rounded-bl-md"
        )}
      >
        {isAudio && audio?.url ? (
          <AudioMessageBubble
            src={audio.url}
            durationSec={audio.duration_sec}
            mime={audio.mime}
            caption={audio.caption}
            isOwn={isOwn}
            downloadable={false}
            played={audioPlayed}
            onPlayed={() => setAudioPlayed(true)}
          />
        ) : isAttachment && attachment ? (
          // Parceiro PODE baixar (downloadable); allowPreview=false (sem dispatcher).
          <AttachmentMessageBubble
            content={attachment}
            downloadable
            allowPreview={false}
            tone={isOwn ? "own" : "other"}
          />
        ) : (
          <>
            {/* O cartão do site fica EM CIMA do texto (a mesma ordem do chat interno). Enquanto a consulta não volta não há
                nada: só o texto. */}
            {linkDoCartao && (
              <CartaoDoLink
                link={linkDoCartao}
                dispensado={linkDispensado}
                origem="portal"
                aparencia={isOwn ? "propria-portal" : "dos-outros"}
                posicao="em-cima"
                id={msg.id}
              />
            )}
            <div className="whitespace-pre-wrap break-words">
              {isAudio ? (
                "🎤 Áudio indisponível"
              ) : (
                <TextoComLinks texto={text} tom={isOwn ? "propria" : "neutro"} />
              )}
            </div>
          </>
        )}
        <div
          className={cn("text-[10px] mt-1", isOwn ? "text-emerald-50/80" : "text-muted-foreground")}
        >
          {new Date(msg.created_at).toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>
    </div>
  );
}

function StartSessionDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onCreated: (session: CustomerSession) => void;
}) {
  const sectorsQuery = usePortalSectors();
  const startMutation = useStartPortalSession();
  const [selectedSector, setSelectedSector] = useState<string>("");

  // Limpa estado quando o dialog fecha — abrir já chega zerado.
  useEffect(() => {
    if (!open) {
      setSelectedSector("");
      startMutation.reset();
    }
    // startMutation.reset é estável; evitamos depender pra não re-disparar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sectors = sectorsQuery.data?.items ?? [];
  const sectorsAvailable = !sectorsQuery.isLoading && !sectorsQuery.isError && sectors.length > 0;
  const canSubmit = selectedSector !== "" && !startMutation.isPending && sectorsAvailable;

  // Patch E2: catch em mutateAsync — `startMutation.isError` já cobre
  // o banner; rejeição não-tratada virava unhandled promise.
  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const session = await startMutation.mutateAsync({
        sector_id: selectedSector,
        delivered_via: "PORTAL",
      });
      onCreated(session);
      onOpenChange(false);
    } catch {
      // erro fica visível via startMutation.isError no banner abaixo
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Abrir nova sessão de atendimento</DialogTitle>
          <DialogDescription>
            Escolha o setor responsável. Sua sessão entra na fila e um atendente entrará em contato
            em instantes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <label className="text-sm font-medium">Setor</label>
          {sectorsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando setores…
            </div>
          ) : sectorsQuery.isError ? (
            <div className="space-y-2">
              <div className="text-sm text-red-700">Erro ao carregar setores.</div>
              <Button variant="outline" size="sm" onClick={() => void sectorsQuery.refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : sectors.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Nenhum setor disponível para atendimento no momento.
            </div>
          ) : (
            <Select value={selectedSector} onValueChange={setSelectedSector}>
              <SelectTrigger aria-label="Selecione um setor">
                <SelectValue placeholder="Selecione um setor…" />
              </SelectTrigger>
              <SelectContent>
                {sectors.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {startMutation.isError && (
            <div className="text-sm text-red-700">Falha ao abrir sessão. Tente novamente.</div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={startMutation.isPending}
          >
            Cancelar
          </Button>
          {/* Patch E7: Iniciar só aparece quando há setores carregados. */}
          {sectorsAvailable && (
            <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
              {startMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Abrindo…
                </>
              ) : (
                <>
                  <SendIcon className="h-4 w-4 mr-2" />
                  Iniciar
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AtendimentoPage() {
  // NÃO usar useSectionTelemetry aqui: ele faz POST no cliente axios
  // INTERNO (/cofre/telemetry), que o parceiro nunca pode chamar. Como
  // o parceiro tem (no máximo) um seweb_auth expirado, o POST tomava 401
  // e o response-interceptor interno fazia window.location.href="/login",
  // expulsando o parceiro da rota /portal/atendimento (bug 2026-06-17).
  // Telemetria de seção é feature de operador interno, não do portal.

  // Patch B1+A4: identidade do parceiro vem do contexto de auth do
  // portal, NÃO da sessão. ownPartnerId derivado da sessão era
  // tautologia (toda mensagem PARTNER da sessão batia).
  const { user: portalUser } = usePortalAuth();
  const ownUserId = portalUser?.id ?? null;

  const sessionsQuery = usePortalSessions();
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  // Cresce a cada clique numa conversa — inclusive na que já está aberta, que não
  // remonta o compositor — e leva o cursor à caixa (ver `useFocoNaCaixa`).
  const [pedidoDeFoco, setPedidoDeFoco] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  // D3 (passo 19): a conversa aberta também mora na URL (`?sessao=<id>`) — o clique no aviso e no push leva a ela, recarregar a mantém e o push
  // do sistema acha a aba certa pelo fim da URL. O estado continua `selectedSessionId` (os testes que leem o fonte da página exigem).
  const [searchParams, setSearchParams] = useSearchParams();
  const sessaoDaUrl = searchParams.get("sessao");
  const { porSessao: naoLidasPorSessao, zerarSessao: zerarNaoLidas } = useNaoLidasDoAtendimento();

  /** Põe (ou tira) o `?sessao=` sem empilhar história (replace) e sem perder os outros parâmetros. */
  const sincronizarUrl = useCallback(
    (id: string | null) => {
      setSearchParams(
        (atual) => {
          const novo = new URLSearchParams(atual);
          if (id) novo.set("sessao", id);
          else novo.delete("sessao");
          return novo;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const abrirSessao = useCallback(
    (id: string) => {
      setSelectedSessionId(id);
      setPedidoDeFoco((n) => n + 1);
      sincronizarUrl(id);
    },
    [sincronizarUrl]
  );

  const sessions = useMemo(() => sessionsQuery.data?.items ?? [], [sessionsQuery.data?.items]);

  // A lista traz só as 50 mais recentes, sem paginação: a conversa aberta pode SAIR dela (outras passaram na frente) enquanto a pessoa
  // lê. Sem lembrar dela, `selectedSession` virava `null` e a tela esquecia que era só leitura (voltavam a caixa de escrever, a triagem e
  // a faixa de chamada, e `GET /{id}/chamada` dava 404 a cada 5 s). Por isso guarda a ÚLTIMA versão que a lista mostrou da conversa
  // aberta e a usa enquanto a lista não a traz: o modo (só leitura), o cabeçalho e as consultas desligadas falham FECHADOS. Vale só para
  // o mesmo id: ao trocar de conversa, a guardada de outra não vale (e uma conversa que a lista nunca mostrou segue como `null`).
  const ultimaSessaoDaLista = useRef<CustomerSession | null>(null);
  const selectedSession = useMemo(() => {
    const daLista = sessions.find((s) => s.id === selectedSessionId) ?? null;
    if (daLista) {
      ultimaSessaoDaLista.current = daLista;
      return daLista;
    }
    const guardada = ultimaSessaoDaLista.current;
    return guardada !== null && guardada.id === selectedSessionId ? guardada : null;
  }, [sessions, selectedSessionId]);

  // Atendimento ativo (01/11/2026): a conta oficial da loja vê, SÓ LEITURA, a conversa que o SE abriu com um colaborador dela. O
  // servidor responde 404 a toda rota por sessão que não seja a das mensagens (chamada, triagem, consentimento, token...), então a tela
  // não pode pedir nada além de ler as mensagens: sem caixa de escrever, sem triagem do bot, sem faixa de chamada.
  const somenteLeitura = selectedSession?.somente_leitura === true;

  // ---- Chamada de voz/vídeo (07/08/2026) --------------------------------
  // Até aqui o portal não tinha NADA de chamada: o atendente clicava "Iniciar
  // chamada", via o cronômetro correndo, e deste lado não acontecia nada.
  // 🔴 Só leitura: `null` desliga a consulta (`enabled: false`). Ela chamaria `GET /{id}/chamada`, que o servidor nega (404) à conta
  // oficial — e o 404 apareceria como erro na conversa.
  const chamadaQuery = useChamadaEmAndamento(somenteLeitura ? null : selectedSessionId);
  // `refetch` é estável no react-query; o objeto da query NÃO é — ele muda de
  // identidade a cada poll (5s). Depender do objeto inteiro recriaria os
  // callbacks a cada ciclo, inclusive o `onDisconnected` que vai para dentro da
  // sala LiveKit.
  const refetchChamada = chamadaQuery.refetch;
  const [salaDaChamada, setSalaDaChamada] = useState<{
    token: string;
    wsUrl: string;
  } | null>(null);
  /** Tela cheia ou miniatura, e desde quando a chamada corre.
   *
   *  Acrescentados em 08/08/2026 junto com a `JanelaDeChamada`: o portal
   *  renderizava a sala numa tira fixa de 38%, sem cronômetro e sem como
   *  minimizar. Quem mais usa o celular é o parceiro, e era ele quem estava
   *  com a interface pior. */
  const [modoDaChamada, setModoDaChamada] = useState<ModoDaJanela>("cheia");
  const [inicioDaChamada, setInicioDaChamada] = useState<string | null>(null);
  const [entrandoNaChamada, setEntrandoNaChamada] = useState(false);
  const [consentimentoAberto, setConsentimentoAberto] = useState(false);
  const [erroDaChamada, setErroDaChamada] = useState<string | null>(null);

  const entrarNaChamada = useCallback(async () => {
    if (!selectedSessionId || entrandoNaChamada) return;
    setEntrandoNaChamada(true);
    setErroDaChamada(null);
    try {
      const t = await portalAtendimentosApi.getLiveKitToken(selectedSessionId);
      setSalaDaChamada({ token: t.token, wsUrl: t.ws_url });
      // Toda chamada nova abre grande; minimizar é escolha de quem já está nela.
      setModoDaChamada("cheia");
      setInicioDaChamada(new Date().toISOString());
    } catch (e: unknown) {
      // 412 = falta consentimento; 409 = não há chamada (ela pode ter sido
      // encerrada entre o poll e o clique). Nenhum dos dois é "erro" do ponto
      // de vista do parceiro, então nenhum vira mensagem de falha.
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 412) {
        setConsentimentoAberto(true);
      } else if (status === 409) {
        void refetchChamada();
      } else {
        setErroDaChamada("Não foi possível entrar na chamada. Tente de novo.");
      }
    } finally {
      setEntrandoNaChamada(false);
    }
  }, [selectedSessionId, entrandoNaChamada, refetchChamada]);

  const sairDaChamada = useCallback(() => {
    // Só desconecta deste lado. Encerrar a chamada inteira é do atendente —
    // o parceiro sair não pode derrubar a sala do outro.
    setSalaDaChamada(null);
    setInicioDaChamada(null);
    void refetchChamada();
  }, [refetchChamada]);

  // Trocar de conversa derruba a sala: senão o parceiro seguiria publicando
  // áudio e vídeo na chamada de uma sessão que ele nem está mais vendo.
  useEffect(() => {
    setSalaDaChamada(null);
    setInicioDaChamada(null);
    setErroDaChamada(null);
  }, [selectedSessionId]);

  // URL → conversa. Reage só a MUDANÇA da URL (`sessaoDaUrl`), nunca a mudança do estado: o `navigate` do router entra em
  // `startTransition` (o App liga `v7_startTransition`) e "Voltar" muda o estado ANTES da URL — um efeito que comparasse a URL
  // com `selectedSessionId` reabriria a conversa que a pessoa acabou de fechar. Só decide com a lista de VERDADE na mão
  // (nem a do cache velho): sessão que não é do parceiro ou que sumiu é ignorada — tira o parâmetro e deixa a lista, sem toast.
  // 🔴 Lista em ERRO não é verdade: o react-query marca `isFetched` com `sessions=[]`, e sem o `isError` o efeito tomaria um
  // `?sessao=` válido por desconhecido e o apagaria — o clique no push perderia a conversa mesmo depois do "Tentar de novo".
  const sessaoDaUrlTratada = useRef<string | null>(null);
  useEffect(() => {
    if (!sessaoDaUrl) {
      sessaoDaUrlTratada.current = null;
      return;
    }
    if (sessaoDaUrl === sessaoDaUrlTratada.current) return;
    if (!sessionsQuery.isFetched || sessionsQuery.isFetching || sessionsQuery.isError) return;
    sessaoDaUrlTratada.current = sessaoDaUrl;
    if (sessions.some((s) => s.id === sessaoDaUrl)) setSelectedSessionId(sessaoDaUrl);
    else sincronizarUrl(null);
  }, [
    sessaoDaUrl,
    sessions,
    sessionsQuery.isFetched,
    sessionsQuery.isFetching,
    sessionsQuery.isError,
    sincronizarUrl,
  ]);

  // A conversa aberta, para o avisador do D3: aberta E aba visível = não avisa, não toca e não soma no número do menu.
  useEffect(() => {
    definirSalaAberta(selectedSessionId);
  }, [selectedSessionId]);
  useEffect(() => () => definirSalaAberta(null), []);

  // Conversa aberta e À VISTA não tem resposta não lida: a busca de mensagens marca `read_at` no servidor (e ele confirma por
  // `atendimento:lidas`). Zera na hora, também quando o número chega DEPOIS de a conversa abrir. Aba escondida: o número é real e fica.
  // 🔴 A visibilidade entra como DEPENDÊNCIA (`useSyncExternalStore`, o molde do `useNaoLidasDoChat`): lida só dentro do efeito, a aba que
  // VOLTA à vista não o rodava de novo e a conversa aberta seguia com "1" na lista e no menu até o servidor confirmar (até 30 s sem socket).
  // O instantâneo é a sala aberta com a aba visível, ou `null`: comparável por valor, não re-renderiza sem mudança.
  const salaAVista = useSyncExternalStore(assinarSalaAberta, salaAbertaVisivel, () => null);
  // Só leitura fica de fora: a leitura da conta oficial não mexe em contador nenhum (o servidor também não marca nada como lido).
  const abertaComNaoLidas =
    selectedSessionId !== null &&
    !somenteLeitura &&
    (naoLidasPorSessao[selectedSessionId] ?? 0) > 0;
  useEffect(() => {
    if (abertaComNaoLidas && selectedSessionId && salaAVista === selectedSessionId) {
      zerarNaoLidas(selectedSessionId);
    }
  }, [abertaComNaoLidas, selectedSessionId, salaAVista, zerarNaoLidas]);

  // Ciclo 2 — paginação por cursor + lazy-load (não carrega o histórico inteiro,
  // crítico pro portal cujas imagens são base64 inline pesado).
  const {
    messages,
    isLoading: messagesLoading,
    isError: messagesError,
    hasMoreOlder,
    loadOlder,
    isLoadingOlder,
  } = usePortalMessages(selectedSessionId);

  const isClosed = selectedSession ? CLOSED_STATUSES.has(selectedSession.status) : false;

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // O que rola dentro do `messagesContainerRef`: UM invólucro que embrulha tudo (sentinela, avisos e mensagens). É o
  // tamanho dele que cresce quando chega um cartão do site ou uma imagem, e o `useGrudadoNoPe` o observa.
  const conteudoRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const prependAnchorRef = useRef<number | null>(null);
  const prevFirstIdRef = useRef<string | null>(null);
  const prevLastIdRef = useRef<string | null>(null);
  const prevSessionIdRef = useRef<string | null>(null);
  // Quão longe do pé a lista estava ANTES de a mensagem nova entrar (medido no render, abaixo).
  const distanciaAntesRef = useRef(0);

  // A lista GRUDADA no pé (`useGrudadoNoPe`, T-R26, 28/10/2026): quem está no fim continua no fim quando o conteúdo
  // cresce (os cartões do site que chegam, às vezes vários no mesmo quadro; a imagem que carrega; o campo que cresce), e
  // quem subiu para ler não é puxado de volta. O hook é dono desse estado; aqui só se chama `irAoPe` para abrir a
  // conversa e para a mensagem nova. Ele liga em `useLayoutEffect`, ANTES do efeito abaixo (a ordem das chamadas manda), e
  // religa sozinho quando o rolável nasce (a conversa só existe depois de escolhida a sessão).
  const { irAoPe } = useGrudadoNoPe(messagesContainerRef, conteudoRef);

  // A distância do pé ANTES de a mensagem nova entrar, medida aqui, no render: o DOM ainda é o do commit anterior. No
  // efeito abaixo a bolha nova já está no `scrollHeight`, e quem lia a 60px do pé pareceria estar a 160.
  //
  // 🔴 Leitura de layout no render é uma troca CONSCIENTE, não descuido: a medida tem de ser do DOM antes da bolha
  // nova, e só o render a vê assim. Fica confinada à troca do último id (uma leitura por mensagem nova, não uma por
  // tecla) e é idempotente: o StrictMode renderiza duas vezes sobre o mesmo DOM e grava o mesmo número. Se a regra
  // `refs` do React Compiler passar a reclamar, o caminho é o `useGrudadoNoPe` expor um `distancia()` (a geometria que
  // ele já conhece) e este bloco virar uma chamada a ele, em vez de ler o DOM aqui.
  const ultimoIdDaLista = messages.length > 0 ? messages[messages.length - 1].id : null;
  if (ultimoIdDaLista !== prevLastIdRef.current && messagesContainerRef.current) {
    distanciaAntesRef.current = distanciaDoPe(messagesContainerRef.current);
  }

  // Sentinela no TOPO → carrega mensagens mais antigas; guarda a altura pra reancorar.
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const c = messagesContainerRef.current;
    if (!sentinel || !c) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreOlder && !isLoadingOlder) {
          prependAnchorRef.current = c.scrollHeight;
          void loadOlder();
        }
      },
      { root: c, rootMargin: "120px" }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [hasMoreOlder, isLoadingOlder, loadOlder]);

  // Prepend de antigas → mantém a posição (sem pulo); primeira carga / troca de
  // sessão → vai pro fundo e gruda; msg nova no fim → fundo só se perto (até 80px, ou própria).
  useLayoutEffect(() => {
    const c = messagesContainerRef.current;
    if (prevSessionIdRef.current !== selectedSessionId) {
      prevSessionIdRef.current = selectedSessionId;
      prevFirstIdRef.current = null;
      prevLastIdRef.current = null;
      prependAnchorRef.current = null;
    }
    if (!c || messages.length === 0) return;
    const firstId = messages[0].id;
    const lastId = messages[messages.length - 1].id;
    const isFirstLoad = prevFirstIdRef.current === null && prevLastIdRef.current === null;

    if (prependAnchorRef.current !== null && firstId !== prevFirstIdRef.current) {
      c.scrollTop += c.scrollHeight - prependAnchorRef.current;
      prependAnchorRef.current = null;
    } else if (isFirstLoad) {
      irAoPe("auto");
    } else if (lastId !== prevLastIdRef.current) {
      const ownLast = messages[messages.length - 1].sender_type === "PARTNER";
      // 🔴 `irAoPe` rola SÓ a lista. O `scrollIntoView` de antes rolava também todo ancestral com sobra.
      if (distanciaAntesRef.current <= NEAR_BOTTOM_THRESHOLD_PX || ownLast) irAoPe("smooth");
    }
    prevFirstIdRef.current = firstId;
    prevLastIdRef.current = lastId;
  }, [messages, selectedSessionId, irAoPe]);

  const handleSessionCreated = (s: CustomerSession) => {
    abrirSessao(s.id);
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-emerald-900">Atendimento</h1>
        <p className="text-sm text-emerald-700/70 mt-1">
          Converse com a equipe interna sobre seus processos, comprovantes e dúvidas.
        </p>
      </div>

      {/* No CELULAR mostra UMA coisa por vez — lista OU conversa. O Augusto:
          "a parte dos atendimentos que foram feitos e a parte do chat está na
          mesma tela… não vejo como algo legal no celular". Empilhar as duas
          deixa o chat espremido embaixo de uma lista que ele já leu.
          No desktop (lg+) as duas continuam lado a lado, que ali funciona.

          A altura usa `dvh` quando há conversa aberta: `vh` no celular inclui a
          faixa atrás da barra de URL, e o rodapé (o compositor) cairia abaixo
          da dobra — foi esse mesmo erro que cortou o campo de mensagem. */}
      <div
        className={`flex flex-col lg:flex-row gap-4 lg:h-[calc(100vh-12rem)] ${
          selectedSessionId ? "h-[calc(100dvh-11rem)]" : ""
        }`}
      >
        {/* Lista de sessões */}
        <Card
          className={`lg:w-72 flex-shrink-0 border-emerald-100/80 bg-white/80 backdrop-blur-sm flex-col overflow-hidden ${
            selectedSessionId ? "hidden lg:flex" : "flex"
          }`}
          data-testid="portal-lista-sessoes"
        >
          <CardHeader className="border-b border-emerald-50 pb-3">
            <CardTitle className="text-base text-emerald-900">Minhas sessões</CardTitle>
            <CardDescription className="text-xs">{sessions.length} no total</CardDescription>
            <Button size="sm" className="mt-2" onClick={() => setDialogOpen(true)}>
              <MessageSquarePlus className="h-4 w-4 mr-1.5" />
              Abrir nova sessão
            </Button>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto">
            {sessionsQuery.isLoading && (
              <div className="flex justify-center py-8 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}

            {sessionsQuery.isError && (
              <div className="p-4 text-sm text-red-700">
                Erro ao carregar suas sessões.
                <Button
                  variant="link"
                  className="px-0 ml-1"
                  onClick={() => void sessionsQuery.refetch()}
                >
                  Tentar de novo
                </Button>
              </div>
            )}

            {!sessionsQuery.isLoading && sessions.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground text-center">
                Você ainda não tem sessões. Clique em "Abrir nova sessão" para começar.
              </div>
            )}

            <ul className="divide-y">
              {sessions.map((s) => {
                const isSelected = s.id === selectedSessionId;
                // Conta oficial vendo a conversa do SE com um colaborador: identificada, sem contador (nada ali é "dela para ler").
                const soLeitura = s.somente_leitura === true;
                const naoLidas = soLeitura ? 0 : (naoLidasPorSessao[s.id] ?? 0);
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => abrirSessao(s.id)}
                      className={cn(
                        "w-full text-left p-3 hover:bg-emerald-50/60 transition-colors",
                        isSelected && "bg-emerald-50 border-l-4 border-emerald-400"
                      )}
                    >
                      {soLeitura ? (
                        <>
                          {/* Conta oficial vendo a conversa de um colaborador (só leitura). O NOME do colaborador tem a linha
                              inteira só para ele: a 1366 px o cartão tem ~262 px úteis e a badge "Em atendimento" sozinha ocupa
                              ~133, então com ela na mesma linha o título cortava em "Conversa com…" e o nome nunca aparecia (D2 do
                              E2E de 02/11/2026). A badge de status e a marca "só leitura" descem para a 2ª linha; setor, atendente
                              e hora ficam na 3ª. A frase fixa `Conversa com {nome} · só leitura` continua inteira no `title` e no
                              texto de leitor de tela (`sr-only`), e o cabeçalho e o rodapé da conversa a mostram. */}
                          <span className="mb-1 flex min-w-0 items-center gap-1.5">
                            <Eye
                              className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                              aria-hidden="true"
                            />
                            <span
                              className="min-w-0 truncate text-xs font-medium"
                              title={tituloDaConversaSoLeitura(s)}
                              data-testid="portal-sessao-titulo"
                            >
                              <span data-testid="portal-sessao-nome">
                                {tituloCurtoDaConversaSoLeitura(s)}
                              </span>
                              <span className="sr-only"> · {MARCA_SO_LEITURA}</span>
                            </span>
                          </span>
                          <div className="mb-1 flex flex-wrap items-center gap-1.5">
                            <Badge variant={statusVariant(s.status)} className="shrink-0">
                              {STATUS_LABEL[s.status]}
                            </Badge>
                            {/* Decorativa: o leitor de tela já leu "só leitura" no título. */}
                            <span
                              className="shrink-0 rounded border px-1.5 text-[10px] font-medium leading-4 text-muted-foreground"
                              aria-hidden="true"
                              data-testid="portal-sessao-marca-so-leitura"
                            >
                              {MARCA_SO_LEITURA}
                            </span>
                          </div>
                          {/* Sem contador de não lidas: a conta oficial não é avisada dessas conversas. */}
                          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span className="truncate">{descricaoDaSessao(s)}</span>
                            <span className="shrink-0">
                              {formatRelative(s.last_message_at ?? s.created_at)}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center justify-between gap-2 mb-1">
                            {/* Setor no lugar do pedaço de UUID. O atendente NÃO
                                entra aqui: o cartão é estreito no celular e o badge
                                de status ao lado já diz se está na fila. */}
                            <span
                              className={cn(
                                "truncate text-xs",
                                naoLidas > 0 ? "font-semibold text-slate-900" : "font-medium"
                              )}
                              data-testid="portal-sessao-titulo"
                            >
                              {tituloDaSessao(s)}
                            </span>
                            <Badge variant={statusVariant(s.status)}>
                              {STATUS_LABEL[s.status]}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                            <span>{formatRelative(s.last_message_at ?? s.created_at)}</span>
                            {naoLidas > 0 && (
                              <>
                                {/* Sem `role="status"` por linha (uma lista de regiões vivas vira ruído): o leitor de tela lê o texto. */}
                                <ContadorDoAtendimento
                                  total={naoLidas}
                                  aria-hidden="true"
                                  data-testid="portal-sessao-nao-lidas"
                                />
                                <span className="sr-only">, {textoDasNaoLidas(naoLidas)}</span>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        {/* Chat principal */}
        <Card
          className={`flex-1 border-emerald-200/40 bg-card/80 backdrop-blur-sm flex-col overflow-hidden ${
            selectedSessionId ? "flex" : "hidden lg:flex"
          }`}
          data-testid="portal-chat"
        >
          {selectedSessionId === null ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground p-8 text-center">
              Selecione uma sessão ou abra uma nova para começar a conversar.
            </div>
          ) : (
            <>
              <CardHeader className="border-b border-emerald-50 pb-3 flex-row items-center justify-between space-y-0">
                <div className="flex items-center gap-2 min-w-0">
                  {/* Só no celular: ali a lista fica ESCONDIDA enquanto a
                      conversa está aberta, então sem este botão o parceiro
                      ficaria preso — teria de usar o "voltar" do navegador, que
                      sai da página inteira. No desktop a lista está ao lado e o
                      botão não faz sentido. */}
                  <Button
                    variant="ghost"
                    className="lg:hidden h-10 w-10 shrink-0 -ml-2"
                    onClick={() => {
                      setSelectedSessionId(null);
                      sincronizarUrl(null);
                    }}
                    aria-label="Voltar para a lista de sessões"
                    data-testid="portal-voltar-lista"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  {/* Setor em cima, atendente embaixo — como o WhatsApp faz com
                      nome + "online". Antes era "Sessão 863c90ed…", que não
                      dizia nem para onde a mensagem foi nem quem responde.

                      Em UMA linha não cabia: medido no celular, o cabeçalho tem
                      221px e "FINANCEIRO · aguardando atendente" era cortado
                      exatamente no atendente — a parte que o parceiro quer
                      saber. Empilhar usa a altura, que estava sobrando.

                      Só o PRIMEIRO nome do atendente chega aqui; o corte é
                      feito no servidor (ver `primeiro_nome_de`). O `title` traz
                      a linha inteira para quem passa o mouse no desktop. */}
                  {/* Só leitura (conta oficial): em cima, de quem é a conversa ("Conversa com Maria · só leitura"); embaixo, o setor
                      e o atendente do SE ("FINANCEIRO · João"). O `title` junta as duas linhas. */}
                  <span
                    className="flex min-w-0 flex-col leading-tight"
                    title={
                      selectedSession
                        ? somenteLeitura
                          ? `${tituloDaConversaSoLeitura(selectedSession)}\n${descricaoDaSessao(selectedSession)}`
                          : descricaoDaSessao(selectedSession)
                        : undefined
                    }
                    data-testid="portal-chat-titulo"
                  >
                    <span className="truncate text-sm font-medium">
                      {selectedSession
                        ? somenteLeitura
                          ? tituloDaConversaSoLeitura(selectedSession)
                          : tituloDaSessao(selectedSession)
                        : `Sessão ${selectedSessionId.slice(0, 8)}…`}
                    </span>
                    {selectedSession && (somenteLeitura || atendenteDaSessao(selectedSession)) && (
                      <span
                        className="truncate text-xs text-muted-foreground"
                        data-testid="portal-chat-atendente"
                      >
                        {somenteLeitura
                          ? descricaoDaSessao(selectedSession)
                          : atendenteDaSessao(selectedSession)}
                      </span>
                    )}
                  </span>
                  {selectedSession && (
                    <Badge variant={statusVariant(selectedSession.status)} className="shrink-0">
                      {STATUS_LABEL[selectedSession.status]}
                    </Badge>
                  )}
                </div>
              </CardHeader>

              {/* Faixa + sala da chamada (07/08/2026). Ficam ENTRE o cabeçalho
                  e as mensagens: a conversa por texto continua visível durante
                  a chamada — é o mesmo princípio do painel do atendente, onde
                  as mensagens enviadas durante a chamada são marcadas com
                  `during_call`. */}
              {/* Aviso ao colaborador (atendimento ativo): a conversa que o SE abriu com ele também é vista pela conta oficial da
                  loja. Persistente (transparência), uma linha, no verde da página — informação, não alerta. Fica ACIMA da faixa de
                  chamada, entre o cabeçalho e as mensagens. */}
              {selectedSession?.conta_oficial_ve && (
                <div
                  role="note"
                  data-testid="portal-chat-conta-oficial-ve"
                  className="flex items-center gap-1.5 border-b border-emerald-100 bg-emerald-50/60 px-3 py-1.5 text-xs text-emerald-900 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200"
                >
                  <Users className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span>A conta oficial da loja também vê esta conversa.</span>
                </div>
              )}

              {/* Só leitura: sem faixa de chamada (a consulta nem nasce) nem erro dela. */}
              {!somenteLeitura && (
                <FaixaDeChamada
                  chamada={chamadaQuery.data}
                  onEntrar={() => void entrarNaChamada()}
                  onPedirConsentimento={() => setConsentimentoAberto(true)}
                  entrando={entrandoNaChamada}
                  jaNaChamada={salaDaChamada !== null}
                />
              )}

              {!somenteLeitura && erroDaChamada && (
                <div
                  role="alert"
                  className="border-b border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
                >
                  {erroDaChamada}
                </div>
              )}

              <CardContent
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 chat-surface"
              >
                {/* Tudo o que rola mora neste invólucro: o `useGrudadoNoPe` observa o tamanho dele. */}
                <div ref={conteudoRef}>
                  {/* sentinela no topo: dispara o carregamento das mais antigas */}
                  <div ref={topSentinelRef} />
                  {isLoadingOlder && (
                    <div className="flex justify-center py-2 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  )}
                  {!isLoadingOlder && hasMoreOlder && messages.length > 0 && (
                    <div className="pb-2 text-center text-[11px] text-muted-foreground">
                      Role para cima para carregar mensagens anteriores
                    </div>
                  )}

                  {messagesLoading && (
                    <div className="flex justify-center text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" />
                    </div>
                  )}

                  {messagesError && (
                    <div className="text-sm text-red-700">Erro ao carregar mensagens.</div>
                  )}

                  {!messagesLoading && messages.length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-8">
                      {somenteLeitura
                        ? "Nenhuma mensagem nesta conversa ainda."
                        : "Nenhuma mensagem ainda. Mande a primeira para abrir o diálogo."}
                    </div>
                  )}

                  {messages.filter(naoEhPerguntaDoBot).map((m) => (
                    <MessageBubble
                      key={m.id}
                      msg={m}
                      isOwn={
                        m.sender_type === "PARTNER" &&
                        ownUserId !== null &&
                        m.sender_id === ownUserId
                      }
                    />
                  ))}
                </div>
              </CardContent>

              {somenteLeitura && selectedSession ? (
                // No lugar do composer, para a conversa não terminar num vazio que pareça "ainda carregando". Tom neutro: é um estado
                // normal da tela, não um erro. Sem triagem do bot (a consulta do estado também é negada à conta oficial).
                <div
                  role="note"
                  data-testid="portal-chat-so-leitura"
                  className="flex items-center justify-center gap-1.5 border-t border-emerald-100/60 bg-muted/40 px-4 py-3 text-xs text-muted-foreground dark:border-emerald-900/40"
                >
                  <Eye className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span className="truncate">{tituloDaConversaSoLeitura(selectedSession)}</span>
                </div>
              ) : (
                <>
                  {/* Story E9.2.3 — Bot Triagem inline (renderiza só quando
                      triagem ACTIVE; o hook retorna null em terminal/ausente). */}
                  <BotTriagePanel sessionId={selectedSessionId} />

                  <CustomerChatComposer
                    sessionId={selectedSessionId}
                    disabled={isClosed}
                    pedidoDeFoco={pedidoDeFoco}
                  />
                </>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Consentimento de gravação — exigido ANTES de entrar na chamada.
          Tratar o 412 do backend como erro na cara do parceiro transformaria
          uma exigência legal prevista em mensagem de falha. */}
      {selectedSessionId && !somenteLeitura && (
        <ConsentModal
          open={consentimentoAberto}
          sessionId={selectedSessionId}
          refuseFallback="end"
          onAccept={() => {
            setConsentimentoAberto(false);
            void refetchChamada();
            void entrarNaChamada();
          }}
          onRefuse={() => setConsentimentoAberto(false)}
        />
      )}

      <StartSessionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={handleSessionCreated}
      />

      {/*
        Story 4.5.2 — modal de pedido de compartilhamento de tela.
        Renderizado fora do main layout pra empilhar como overlay.
        ``incomingScreenShareRequest`` é null hoje (useCallDataEvents é
        placeholder em PRD 0); em PRD 3 vai popular automaticamente via
        data channel. Mantemos o componente já wired pra que a story
        funcione end-to-end no momento em que o transport ligar.
      */}

      {/*
        A chamada como JANELA, igual ao painel do atendente.

        Até 08/08/2026 o portal renderizava a sala numa TIRA de 38% da altura,
        fixa, acima da conversa. O Augusto, testando pelo celular: "fica uma
        janelinha em cima da conversa … seria interessante se a parte do vídeo
        pudesse ocupar a tela inteira do celular, e se a pessoa quiser
        minimizar, volta do jeito que está hoje."

        O atendente já tinha ganhado isso; o parceiro tinha ficado para trás — e
        ele é justamente quem mais usa o celular. Usar o MESMO componente evita
        que os dois lados voltem a divergir.
      */}
      {salaDaChamada && (
        <JanelaDeChamada
          chamada={{
            callSessionId: "",
            token: salaDaChamada.token,
            wsUrl: salaDaChamada.wsUrl,
          }}
          startedAt={inicioDaChamada ?? new Date().toISOString()}
          onEnd={sairDaChamada}
          titulo="Chamada com o atendente"
          modo={modoDaChamada}
          onModoChange={setModoDaChamada}
        >
          {/* DENTRO da sala: é o que dá ao gate acesso ao `useRoomContext()`
              para publicar a tela. Fora dela, ele capturava o stream e não
              tinha onde entregá-lo. */}
          <PartnerScreenShareGate sessionId={selectedSessionId} />
        </JanelaDeChamada>
      )}
    </div>
  );
}

/**
 * Subscriber do `useCallDataEvents` que extrai o último
 * `screen_share_request` em aberto e renderiza o banner do parceiro.
 *
 * Mantido como subcomponente pra evitar acoplar a tela inteira ao hook
 * — também facilita teste isolado quando o hook ficar real em PRD 3.
 */
function PartnerScreenShareGate({ sessionId }: { sessionId: string }) {
  const { events } = useCallDataEvents(sessionId);
  const [dismissedEventId, setDismissedEventId] = useState<string | null>(null);

  // Pega o request mais recente (events vêm em ordem de chegada).
  const latestRequest = useMemo(() => {
    for (let i = events.length - 1; i >= 0; i--) {
      const e = events[i];
      if (e.eventType === "screen_share_request") {
        return {
          eventId: e.id,
          message:
            typeof e.payload.message === "string"
              ? e.payload.message
              : "O atendente pediu para ver a sua tela.",
        };
      }
    }
    return null;
  }, [events]);

  const incomingRequest =
    latestRequest !== null && latestRequest.eventId !== dismissedEventId ? latestRequest : null;

  // A sala vem do contexto do `<LiveKitRoom>`: este componente é renderizado
  // DENTRO do `<InlineCallRoom>` desde 07/08/2026. Antes ficava fora, e por isso
  // não tinha como publicar nada.
  const sala = useRoomContext();

  const handleAccept = useCallback(
    async (stream: MediaStream) => {
      // PUBLICA a tela na sala. Até 07/08/2026 este callback capturava o stream
      // e o JOGAVA FORA com um `console.info` — o parceiro via o navegador
      // pedir permissão, escolhia a janela, e nada era transmitido. Mesma
      // classe do botão "Iniciar chamada" que só ligava um cronômetro: a tela
      // dizia que funcionava.
      const trilha = stream.getVideoTracks()[0];
      if (!trilha) {
        throw new Error("Compartilhamento sem trilha de vídeo.");
      }
      await sala.localParticipant.publishTrack(trilha, {
        source: Track.Source.ScreenShare,
      });

      // Parar de compartilhar pelo botão NATIVO do navegador (fora da nossa UI)
      // dispara `ended` na trilha. Sem isto, o atendente continuaria vendo o
      // último quadro congelado, achando que ainda está sendo compartilhado.
      trilha.addEventListener("ended", () => {
        void sala.localParticipant.unpublishTrack(trilha);
      });
    },
    [sala]
  );

  return (
    <ScreenShareRequestBanner
      role="partner"
      sessionId={sessionId}
      incomingRequest={incomingRequest}
      onAccept={handleAccept}
      onDismiss={() => latestRequest !== null && setDismissedEventId(latestRequest.eventId)}
    />
  );
}
